import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Grid,
    Alert,
    Switch,
    FormControlLabel,
    Divider,
    useTheme,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    Radio
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublishIcon from '@mui/icons-material/Publish';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';

const ReportViewer = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { reportId } = useParams();
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { enqueueSnackbar } = useSnackbar();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
    const [unpublishStatus, setUnpublishStatus] = useState('draft');
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [publishDate, setPublishDate] = useState('');

    // Editable fields
    const [teacherComment, setTeacherComment] = useState('');
    const [classParticipationScore, setClassParticipationScore] = useState(0);
    const [achievementScore, setAchievementScore] = useState(0);
    const [comprehensionScore, setComprehensionScore] = useState(0);
    const [growthScore, setGrowthScore] = useState(0);
    const [homeworkScore, setHomeworkScore] = useState(0);
    const [strengths, setStrengths] = useState([]);
    const [improvements, setImprovements] = useState([]);

    // Editable metadata fields
    const [courseName, setCourseName] = useState('');
    const [grade, setGrade] = useState('');
    const [semesterYear, setSemesterYear] = useState('');
    const [semesterSeason, setSemesterSeason] = useState('');
    const [teacherName, setTeacherName] = useState('');
    const [examScore, setExamScore] = useState('');

    // Iframe ref for dynamic height
    const iframeRef = useRef(null);

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    const fetchReport = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportId}`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setReport(response.data);
            setTeacherComment(response.data.teacher_comment || '');
            setClassParticipationScore(response.data.class_participation_score || 0);
            setAchievementScore(response.data.achievement_score || 0);
            setComprehensionScore(response.data.comprehension_score || 0);
            setGrowthScore(response.data.growth_score || 0);
            setHomeworkScore(response.data.homework_score || 0);
            setStrengths(
                response.data.report_data?.edited?.strengths
                ?? response.data.report_data?.strengths
                ?? []
            );
            setImprovements(
                response.data.report_data?.edited?.improvements
                ?? response.data.report_data?.improvements
                ?? []
            );

            // Initialize metadata fields with edited values if present, otherwise use original
            setCourseName(response.data.edited?.course_name || response.data.course?.name || '');
            setGrade(response.data.edited?.grade || response.data.student?.grade || '');
            setSemesterYear(response.data.edited?.semester_year || response.data.semester?.year || '');
            setSemesterSeason(response.data.edited?.semester_season || response.data.semester?.season || '');
            setTeacherName(response.data.edited?.teacher_name || response.data.teacher || '');
            setExamScore(response.data.exam_score || '');
            setLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 404) {
                setError('Report not found');
                setLoading(false);
            } else {
                setError('Failed to load report');
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Reports');
        fetchReport();
    }, [reportId]);

    const handleSave = async () => {
        // Validate scores
        if (
            classParticipationScore < 3.0 || classParticipationScore > 5.0 ||
            achievementScore < 3.0 || achievementScore > 5.0 ||
            comprehensionScore < 3.0 || comprehensionScore > 5.0 ||
            growthScore < 3.0 || growthScore > 5.0 ||
            homeworkScore < 2.0 || homeworkScore > 5.0
        ) {
            enqueueSnackbar('Invalid score ranges. Check your inputs.', { variant: 'error' });
            return;
        }

        if (strengths.length < 3 || strengths.length > 5) {
            enqueueSnackbar('Strengths must have between 3 and 5 items.', { variant: 'error' });
            return;
        }
        if (strengths.some(s => !s.trim())) {
            enqueueSnackbar('Strengths cannot contain empty items.', { variant: 'error' });
            return;
        }
        if (improvements.length < 2 || improvements.length > 3) {
            enqueueSnackbar('Improvements must have between 2 and 3 items.', { variant: 'error' });
            return;
        }
        if (improvements.some(s => !s.trim())) {
            enqueueSnackbar('Improvements cannot contain empty items.', { variant: 'error' });
            return;
        }

        setSaving(true);
        setError('');

        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportId}`,
                {
                    teacher_comment: teacherComment,
                    class_participation_score: parseFloat(classParticipationScore),
                    achievement_score: parseFloat(achievementScore),
                    comprehension_score: parseFloat(comprehensionScore),
                    growth_score: parseFloat(growthScore),
                    homework_score: parseFloat(homeworkScore),
                    course_name: courseName,
                    grade: grade,
                    semester_year: semesterYear ? parseInt(semesterYear) : undefined,
                    semester_season: semesterSeason,
                    teacher_name: teacherName,
                    exam_score: examScore,
                    strengths: strengths.map(s => s.trim()),
                    improvements: improvements.map(s => s.trim()),
                    status: 'draft'
                },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                        'Content-Type': 'application/json'
                    }
                }
            );

            enqueueSnackbar('Report saved successfully', { variant: 'success' });
            setEditMode(false);
            fetchReport(); // Refresh data
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar(
                    error.response?.data?.error || 'Failed to save report',
                    { variant: 'error' }
                );
            }
        } finally {
            setSaving(false);
        }
    };

    const updateStrength = (idx, value) =>
        setStrengths(arr => arr.map((s, i) => (i === idx ? value : s)));
    const addStrength = () => setStrengths(arr => [...arr, '']);
    const removeStrength = (idx) =>
        setStrengths(arr => arr.filter((_, i) => i !== idx));

    const updateImprovement = (idx, value) =>
        setImprovements(arr => arr.map((s, i) => (i === idx ? value : s)));
    const addImprovement = () => setImprovements(arr => [...arr, '']);
    const removeImprovement = (idx) =>
        setImprovements(arr => arr.filter((_, i) => i !== idx));

    const handlePrint = () => {
        if (report?.report_html) {
            const printWindow = window.open('', '_blank');
            printWindow.document.write(report.report_html);
            printWindow.document.close();
            printWindow.onload = () => {
                // Wait 2 seconds for chart to render before printing
                setTimeout(() => {
                    printWindow.print();
                }, 1000);
            };
        }
    };

    const handleCancel = () => {
        // Reset to original values
        setTeacherComment(report.teacher_comment || '');
        setClassParticipationScore(report.class_participation_score || 0);
        setAchievementScore(report.achievement_score || 0);
        setComprehensionScore(report.comprehension_score || 0);
        setGrowthScore(report.growth_score || 0);
        setHomeworkScore(report.homework_score || 0);
        setStrengths(
            report.report_data?.edited?.strengths
            ?? report.report_data?.strengths
            ?? []
        );
        setImprovements(
            report.report_data?.edited?.improvements
            ?? report.report_data?.improvements
            ?? []
        );

        // Reset metadata fields
        setCourseName(report.edited?.course_name || report.course?.name || '');
        setGrade(report.edited?.grade || report.student?.grade || '');
        setSemesterYear(report.edited?.semester_year || report.semester?.year || '');
        setSemesterSeason(report.edited?.semester_season || report.semester?.season || '');
        setTeacherName(report.edited?.teacher_name || report.teacher || '');
        setExamScore(report.exam_score || '');

        setEditMode(false);
    };

    const handleRegenerate = async () => {
        if (!window.confirm('Are you sure you want to regenerate this report?')) {
            return;
        }

        setRegenerating(true);
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportId}/retry`,
                {},
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );

            enqueueSnackbar('Report regeneration started! Redirecting...', { variant: 'info' });

            // Get batch_id from report_data if available, otherwise create progress view
            setTimeout(() => {
                if (report?.report_data?.batch_id) {
                    navigate(`/admin/reports/progress/${report.report_data.batch_id}`);
                } else {
                    // Navigate back to reports list with status polling
                    navigate('/admin/reports');
                }
            }, 1500);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar(
                    error.response?.data?.error || 'Failed to regenerate report',
                    { variant: 'error' }
                );
                setRegenerating(false);
            }
        }
    };

    const handleOpenPublishDialog = () => {
        // Set default to current local datetime
        const now = new Date();
        const localDatetime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        setPublishDate(localDatetime);
        setPublishDialogOpen(true);
    };

    const handleClosePublishDialog = () => {
        setPublishDialogOpen(false);
    };

    const handlePublish = async () => {
        if (!publishDate) {
            enqueueSnackbar('Please select a release date', { variant: 'error' });
            return;
        }

        setPublishing(true);
        handleClosePublishDialog();
        try {
            // Convert local datetime to UTC ISO string
            const localDate = new Date(publishDate);
            const utcDate = localDate.toISOString();

            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportId}/publish`,
                { first_published_at: utcDate },
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            enqueueSnackbar('Report published successfully', { variant: 'success' });
            fetchReport();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar(
                    error.response?.data?.error || 'Failed to publish report',
                    { variant: 'error' }
                );
            }
        } finally {
            setPublishing(false);
        }
    };

    const handleOpenUnpublishDialog = () => {
        setUnpublishStatus('draft');
        setUnpublishDialogOpen(true);
    };

    const handleCloseUnpublishDialog = () => {
        setUnpublishDialogOpen(false);
    };

    const handleUnpublish = async () => {
        setPublishing(true);
        handleCloseUnpublishDialog();
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportId}/unpublish`,
                { status: unpublishStatus },
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            enqueueSnackbar(`Report unpublished and set to ${unpublishStatus}`, { variant: 'success' });
            fetchReport();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar(
                    error.response?.data?.error || 'Failed to unpublish report',
                    { variant: 'error' }
                );
            }
        } finally {
            setPublishing(false);
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return (
            <Box m="20px" marginX={3} marginY={3}>
                <Header title="View Report" subtitle="Student Report Details" />
                <Alert severity="error">{error}</Alert>
                <Button onClick={() => navigate('/admin/reports')} sx={{ mt: 2 }}>
                    Back to Reports
                </Button>
            </Box>
        );
    }

    if (!report || report.status === 'pending' || report.status === 'processing') {
        return (
            <Box m="20px" marginX={3} marginY={3}>
                <Header title="View Report" subtitle="Student Report Details" />
                <Alert severity="info">
                    This report is still being generated. Please wait...
                </Alert>
                <Button onClick={() => navigate('/admin/reports')} sx={{ mt: 2 }}>
                    Back to Reports
                </Button>
            </Box>
        );
    }

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header
                title="View Report"
                subtitle={`${report.student?.name || 'Student'}`}
            />

            {/* Action Bar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/admin/reports')}
                    >
                        Back to Reports
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                    >
                        Print Report
                    </Button>
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<RefreshIcon />}
                        onClick={handleRegenerate}
                        disabled={regenerating}
                    >
                        {regenerating ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                    {/* Publish/Unpublish Button - only for completed or draft reports */}
                    {(report?.status === 'completed' || report?.status === 'draft') && (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<PublishIcon />}
                            onClick={handleOpenPublishDialog}
                            disabled={publishing}
                        >
                            {publishing ? 'Publishing...' : 'Publish'}
                        </Button>
                    )}
                    {report?.status === 'published' && (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<RotateLeftIcon />}
                            onClick={handleOpenUnpublishDialog}
                            disabled={publishing}
                        >
                            {publishing ? 'Unpublishing...' : 'Unpublish'}
                        </Button>
                    )}
                </Box>
                <Box display="flex" gap={2}>
                    {/* Edit Button */}
                    {!editMode ? (
                        <Tooltip
                            title={report?.status === 'published' ? 'Unpublish this report before editing' : ''}
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<EditIcon />}
                                    onClick={() => setEditMode(true)}
                                    disabled={report?.status === 'published'}
                                >
                                    Edit Report
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Box display="flex" gap={1}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Report Info */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Course"
                            fullWidth
                            value={courseName}
                            onChange={(e) => setCourseName(e.target.value)}
                            disabled={!editMode}
                            size="small"
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Grade"
                            fullWidth
                            value={grade}
                            onChange={(e) => setGrade(e.target.value)}
                            disabled={!editMode}
                            size="small"
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Semester Year"
                            type="number"
                            fullWidth
                            value={semesterYear}
                            onChange={(e) => setSemesterYear(e.target.value)}
                            disabled={!editMode}
                            size="small"
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Season"
                            fullWidth
                            value={semesterSeason}
                            onChange={(e) => setSemesterSeason(e.target.value)}
                            disabled={!editMode}
                            size="small"
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Teacher"
                            fullWidth
                            value={teacherName}
                            onChange={(e) => setTeacherName(e.target.value)}
                            disabled={!editMode}
                            size="small"
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label={`${report?.report_type === 'midterm' ? 'Midterm' : 'Final'} Exam Score`}
                            fullWidth
                            value={examScore}
                            onChange={(e) => setExamScore(e.target.value)}
                            disabled={!editMode}
                            size="small"
                            placeholder="e.g., 85, A+"
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Teacher Comment */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" mb={2}>Teacher Comments</Typography>
                <TextField
                    multiline
                    rows={12}
                    fullWidth
                    value={teacherComment}
                    onChange={(e) => setTeacherComment(e.target.value)}
                    disabled={!editMode}
                    placeholder="No comments yet..."
                    sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: theme.palette.text.primary,
                            color: theme.palette.text.primary
                        }
                    }}
                />
            </Paper>

            {/* Strengths */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="baseline" gap={1} mb={2}>
                    <Typography variant="h6">Strengths</Typography>
                    <Typography variant="caption" color="text.secondary">
                        ({strengths.length}/5, min 3)
                    </Typography>
                </Box>
                {strengths.map((s, idx) => (
                    <Box key={`strength-${idx}`} display="flex" gap={1} mb={1.5} alignItems="flex-start">
                        <TextField
                            multiline
                            rows={2}
                            fullWidth
                            value={s}
                            onChange={(e) => updateStrength(idx, e.target.value)}
                            disabled={!editMode}
                            placeholder={`Strength ${idx + 1}`}
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        <IconButton
                            onClick={() => removeStrength(idx)}
                            disabled={!editMode || strengths.length <= 3}
                            aria-label="Remove strength"
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Box>
                ))}
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={addStrength}
                    disabled={!editMode || strengths.length >= 5}
                    sx={{ mt: 1 }}
                >
                    Add Strength
                </Button>
            </Paper>

            {/* Improvements */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="baseline" gap={1} mb={2}>
                    <Typography variant="h6">Improvements</Typography>
                    <Typography variant="caption" color="text.secondary">
                        ({improvements.length}/3, min 2)
                    </Typography>
                </Box>
                {improvements.map((s, idx) => (
                    <Box key={`improvement-${idx}`} display="flex" gap={1} mb={1.5} alignItems="flex-start">
                        <TextField
                            multiline
                            rows={2}
                            fullWidth
                            value={s}
                            onChange={(e) => updateImprovement(idx, e.target.value)}
                            disabled={!editMode}
                            placeholder={`Improvement ${idx + 1}`}
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        <IconButton
                            onClick={() => removeImprovement(idx)}
                            disabled={!editMode || improvements.length <= 2}
                            aria-label="Remove improvement"
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Box>
                ))}
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={addImprovement}
                    disabled={!editMode || improvements.length >= 3}
                    sx={{ mt: 1 }}
                >
                    Add Improvement
                </Button>
            </Paper>

            {/* Scores */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" mb={2}>Performance Scores</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Class Participation (3.0 - 5.0)"
                            type="number"
                            fullWidth
                            value={classParticipationScore}
                            onChange={(e) => setClassParticipationScore(e.target.value)}
                            disabled={!editMode}
                            inputProps={{ min: 3.0, max: 5.0, step: 0.1 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Achievement (3.0 - 5.0)"
                            type="number"
                            fullWidth
                            value={achievementScore}
                            onChange={(e) => setAchievementScore(e.target.value)}
                            disabled={!editMode}
                            inputProps={{ min: 3.0, max: 5.0, step: 0.1 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Comprehension (3.0 - 5.0)"
                            type="number"
                            fullWidth
                            value={comprehensionScore}
                            onChange={(e) => setComprehensionScore(e.target.value)}
                            disabled={!editMode}
                            inputProps={{ min: 3.0, max: 5.0, step: 0.1 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Growth (3.0 - 5.0)"
                            type="number"
                            fullWidth
                            value={growthScore}
                            onChange={(e) => setGrowthScore(e.target.value)}
                            disabled={!editMode}
                            inputProps={{ min: 3.0, max: 5.0, step: 0.1 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Homework (2.0 - 5.0)"
                            type="number"
                            fullWidth
                            value={homeworkScore}
                            onChange={(e) => setHomeworkScore(e.target.value)}
                            disabled={!editMode}
                            inputProps={{ min: 2.0, max: 5.0, step: 0.1 }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Print Preview */}
            {report.report_html && (
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" mb={2}>Print Preview</Typography>
                    <Box
                        sx={{
                            border: `1px solid ${colors.grey[700]}`,
                            borderRadius: 1,
                            overflow: 'hidden'
                        }}
                    >
                        <iframe
                            ref={iframeRef}
                            srcDoc={report.report_html}
                            onLoad={() => {
                                if (iframeRef.current) {
                                    try {
                                        const iframeDoc = iframeRef.current.contentWindow.document;
                                        const height = Math.max(
                                            iframeDoc.body.scrollHeight,
                                            iframeDoc.documentElement.scrollHeight
                                        );
                                        iframeRef.current.style.height = `${height + 20}px`;
                                    } catch (e) {
                                        console.error('Error resizing iframe:', e);
                                    }
                                }
                            }}
                            style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: '#fff',
                                display: 'block'
                            }}
                            title="Report Preview"
                        />
                    </Box>
                </Paper>
            )}

            {/* Publish Dialog */}
            <Dialog open={publishDialogOpen} onClose={handleClosePublishDialog}>
                <DialogTitle>Publish Report</DialogTitle>
                <DialogContent>
                    {report?.status !== 'completed' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This report is currently in "{report?.status}" status.
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Set the release date for this report. It will be visible to parents.
                    </Typography>
                    <TextField
                        label="Release Date"
                        type="datetime-local"
                        fullWidth
                        value={publishDate}
                        onChange={(e) => setPublishDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePublishDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePublish}
                        variant="contained"
                        color="success"
                        startIcon={<PublishIcon />}
                    >
                        Publish
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unpublish Dialog */}
            <Dialog open={unpublishDialogOpen} onClose={handleCloseUnpublishDialog}>
                <DialogTitle>Unpublish Report</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        This report will no longer be visible to parents. Select the status to revert to:
                    </Typography>
                    <RadioGroup
                        value={unpublishStatus}
                        onChange={(e) => setUnpublishStatus(e.target.value)}
                    >
                        <FormControlLabel value="draft" control={<Radio />} label="Draft" />
                        <FormControlLabel value="completed" control={<Radio />} label="Completed" />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUnpublishDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUnpublish}
                        variant="contained"
                        color="warning"
                        startIcon={<RotateLeftIcon />}
                    >
                        Unpublish
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReportViewer;
